import { useEffect, useRef, type ReactNode } from 'react';
import { create } from 'zustand';
import { Modal } from './Modal.js';

/**
 * "Are you sure?", in the portal's own window (owner, 27 September 2026).
 *
 * The browser's confirm() looked like nothing else on the page, offered "OK" for every action,
 * and put the question and its consequence in one run-on sentence. This asks the question as
 * the title, says what happens underneath, and names the button after the action ("Delete",
 * "Publish"). A destructive action is red, and Cancel has the focus, so Enter never deletes.
 *
 *   if (await confirmAction({ title: `Delete ${t.name}?`, body: 'Calls stop using it within a minute.',
 *                             action: 'Delete', danger: true })) remove.mutate(t.id);
 */
export interface ConfirmRequest {
  /** The question. */
  title: string;
  /** What happens if they go ahead, when that is not obvious from the question. */
  body?: ReactNode;
  /** The button: the action itself, never "OK". */
  action: string;
  /** Deleting, revoking, turning something off: the button is red and does not take the focus. */
  danger?: boolean;
}

interface ConfirmState {
  pending: (ConfirmRequest & { resolve: (ok: boolean) => void }) | null;
  ask: (r: ConfirmRequest) => Promise<boolean>;
  answer: (ok: boolean) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  pending: null,
  ask: (r) => new Promise<boolean>((resolve) => {
    // One question at a time: a second one withdraws the first.
    get().pending?.resolve(false);
    set({ pending: { ...r, resolve } });
  }),
  answer: (ok) => {
    const p = get().pending;
    set({ pending: null });
    p?.resolve(ok);
  },
}));

/** Ask before doing something that cannot be taken back. Resolves true to go ahead. */
export const confirmAction = (r: ConfirmRequest): Promise<boolean> => useConfirmStore.getState().ask(r);

/** Mounted once beside the Toaster (app/App.tsx). */
export function ConfirmHost() {
  const pending = useConfirmStore((s) => s.pending);
  const answer = useConfirmStore((s) => s.answer);
  const go = useRef<HTMLButtonElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!pending) return;
    // After the Modal has focused itself: the safe button takes the focus from it.
    const id = requestAnimationFrame(() => (pending.danger ? cancel : go).current?.focus());
    return () => cancelAnimationFrame(id);
  }, [pending]);

  if (!pending) return null;
  return (
    <Modal title={pending.title} size="sm" onClose={() => answer(false)}>
      <div className="arc-confirm">
        {pending.body !== undefined && <p className="arc-muted arc-confirm-body">{pending.body}</p>}
        <div className="arc-confirm-actions">
          <button ref={cancel} type="button" className="arc-btn" onClick={() => answer(false)}>Cancel</button>
          <button ref={go} type="button" className={`arc-btn ${pending.danger ? 'arc-btn-danger-solid' : 'arc-btn-primary'}`}
            onClick={() => answer(true)}>{pending.action}</button>
        </div>
      </div>
    </Modal>
  );
}
